import { Form } from '@/components/ui/forms/form';
import { ptBrCommon } from '@/lib/pt-br/messages';
import { LoginUserInput } from '@/types';
import type { SubmitHandler, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import Input from '@/components/ui/forms/input';
import Button from '@/components/ui/button';
import Bell from '@/assets/icons/bell.svg';
import { Image } from '@/components/ui/image';
import { createMachine } from 'xstate';
import { useMachine } from '@xstate/react';
import { CloseRedIcon } from '@/components/icons/product/close-red-icon';
import { VerifiedIcon } from '@/components/icons/product/verified-icon';
import SquaredBox from '@/components/ui/squared-box';
import {
  useCreateProductAvailabilityNotification,
} from '@/framework/useProductAvailabilityNotifications';
import { useAtom } from 'jotai';
import { userAtom } from '@/framework/user-store';
import { useEffect } from 'react';
import { toast } from 'react-toastify';

const emailFormValidation = yup.object().shape({
  email: yup
    .string()
    .email('error-email-format')
    .required('error-email-required'),
});

type EmailFormValues = Pick<LoginUserInput, 'email'>;

function EmailFormFields({
  email,
  isLoading,
  register,
  reset,
  formState: { errors },
}: Readonly<{
  email?: string;
  isLoading?: boolean;
} & Pick<UseFormReturn<EmailFormValues>, 'register' | 'reset' | 'formState'>>) {
  // userAtom loads async after mount; defaultValues alone won't update the field
  useEffect(() => {
    reset({ email: email ?? '' });
  }, [email, reset]);

  return (
    <div className="flex flex-col items-center gap-4">
      <Input
        className="w-full"
        type="email"
        autoComplete="email"
        spellCheck={false}
        placeholder="Insira seu e-mail"
        {...register('email')}
        error={ptBrCommon(errors.email?.message!)}
      />
      <Button
        type="submit"
        className="w-4-12 text-sm tracking-[0.2px] rounded-3xl"
        loading={isLoading}
        disabled={isLoading}
      >
        <Image src={Bell} alt="" width={14} height={18} unoptimized={true} />
        <span className="ml-1">Avise-me!</span>
      </Button>
    </div>
  );
}

function EmailForm({
  email,
  onSubmit,
  isLoading,
  serverError,
}: Readonly<{
  email?: string;
  onSubmit: SubmitHandler<EmailFormValues>;
  isLoading?: boolean;
  serverError?: any;
}>) {
  return (
    <Form
      onSubmit={onSubmit}
      useFormProps={{
        defaultValues: { email: email ?? '' },
      }}
      validationSchema={emailFormValidation}
      serverError={serverError && ptBrCommon(serverError)}
      className="text-left w-full"
    >
      {(methods) => (
        <EmailFormFields email={email} isLoading={isLoading} {...methods} />
      )}
    </Form>
  );
}

function BoxMissingProduct({
  emailFormHandle,
  isLoading,
  email,
}: Readonly<{
  emailFormHandle: SubmitHandler<Pick<LoginUserInput, 'email'>>;
  isLoading?: boolean;
  email?: string;
}>) {
  return (
    <SquaredBox>
      <SquaredBox.Header
        title="Produto Indisponível"
        icon={<CloseRedIcon />}
        className="bg-[rgba(240,86,122,0.1)]"
        titleStyle="text-red-500"
      />
      <SquaredBox.Content className="items-center">
        <p className="py-6 w-full">
          Ops! Parece que estamos sem estoque deste produto no momento. Informe
          seu e-mail para avisarmos quando o item voltar.
        </p>

        <EmailForm
          email={email}
          onSubmit={emailFormHandle}
          isLoading={isLoading}
        />
      </SquaredBox.Content>
    </SquaredBox>
  );
}

function BoxConfirmation({ handleConfirm }: Readonly<{ handleConfirm: () => void }>) {
  return (
    <SquaredBox>
      <SquaredBox.Header
        title="Produto Indisponível"
        icon={<VerifiedIcon />}
        className="bg-[#E8F2FB]"
        titleStyle="text-accent"
      />
      <SquaredBox.Content className="items-center">
        <p className="py-6 w-full">
          Avisaremos assim que o produto estiver disponível novamente em nosso
          site.
        </p>

        <Button
          className="w-max min-w-[120px] text-sm tracking-[0.2px]"
          onClick={handleConfirm}
        >
          <span>OK!</span>
        </Button>
      </SquaredBox.Content>
    </SquaredBox>
  );
}

const productStockMachine = createMachine({
  id: 'productStock',
  initial: 'email',
  states: {
    email: { on: { NEXT: 'confirmation' } },
    confirmation: { on: { BACK: 'email' } },
  },
});

function RenderFormSteps({ productId }: Readonly<{ productId: number }>) {
  const [state, send] = useMachine(productStockMachine);
  const [user] = useAtom(userAtom);
  const { mutate, isLoading } = useCreateProductAvailabilityNotification();

  const emailFormHandle: SubmitHandler<Pick<LoginUserInput, 'email'>> = ({
    email,
  }) => {
    if (isLoading) return;

    const notifyEmail = (user?.email || email || '').trim();
    if (!notifyEmail) return;

    mutate(
      { email: notifyEmail, product_id: productId },
      {
        onSuccess: () => send({ type: 'NEXT' }),
        onError: () => {
          toast.error('Não foi possível registrar o aviso. Tente novamente.');
        },
      },
    );
  };

  const handleConfirm = () => {
    send({ type: 'BACK' });
  };

  return (
    <div className="w-full mt-6">
      {state.matches('email') && (
        <BoxMissingProduct
          emailFormHandle={emailFormHandle}
          isLoading={isLoading}
          email={user?.email}
        />
      )}

      {state.matches('confirmation') && (
        <BoxConfirmation handleConfirm={handleConfirm} />
      )}
    </div>
  );
}

export default function ProductOutOfStock({ productId }: Readonly<{ productId: number }>) {
  return <RenderFormSteps productId={productId} />;
}
